/** Dashed scales with line width; dotted is a zero-length dash drawn by its round caps. */

/** Stroke styles an element (node border or link line) can carry. */
export type StrokeLineStyle = 'solid' | 'dashed' | 'dotted';

/** The cap styles the patterns below depend on. */
export type StrokeLineCap = 'butt' | 'round';

/** Dash length of a dashed stroke, as a multiple of the line width. */
export const DASH_SIZE = 3;

/** Gap length of a dashed stroke, as a multiple of the line width. */
export const DASH_GAP_SIZE = 1;

/** Constant regardless of line width: the round caps draw the dot. */
export const DOT_DASH_LENGTH = 0;

/** Gap length of a dotted stroke, as a multiple of the line width. */
export const DOT_GAP_SIZE = 2;

/** Keeps a zero/ghost width from collapsing the GAP and pinning a sampler. */
const MIN_PATTERN_WIDTH = 0.25;

function normalizeStyle(style: string | null | undefined): StrokeLineStyle {
  const s = String(style ?? 'solid').toLowerCase();
  return s === 'dashed' || s === 'dotted' ? s : 'solid';
}

/** [dash, gap] in the same units as `lineWidth`, or null for solid. */
export function strokeDashPattern(
  style: string | null | undefined,
  lineWidth: number
): [number, number] | null {
  const width = Math.max(Number(lineWidth) || 0, MIN_PATTERN_WIDTH);
  switch (normalizeStyle(style)) {
    case 'dashed':
      return [DASH_SIZE * width, DASH_GAP_SIZE * width];
    case 'dotted':
      return [DOT_DASH_LENGTH, DOT_GAP_SIZE * width];
    default:
      return null;
  }
}

/** Butt for dashed (caps would close the gaps), round for dotted. */
export function strokeLineCap(style: string | null | undefined): StrokeLineCap {
  return normalizeStyle(style) === 'dotted' ? 'round' : 'butt';
}

/** SVG `stroke-dasharray`, or undefined for solid. */
export function strokeDashArray(
  style: string | null | undefined,
  lineWidth: number
): string | undefined {
  const pattern = strokeDashPattern(style, lineWidth);
  if (!pattern) return undefined;
  const round = (n: number) => Number(n.toFixed(3));
  return `${round(pattern[0])} ${round(pattern[1])}`;
}
